<script setup lang="ts">
import { computed } from 'vue'

export interface BaseButtonProps {
  variant?: 'primary' | 'secondary' | 'danger' | 'icon' | 'fab'
  size?: 'sm' | 'md' | 'lg'
  disabled?: boolean
  loading?: boolean
  icon?: string
  iconPosition?: 'left' | 'right'
  type?: 'button' | 'submit' | 'reset'
  ariaLabel?: string
}

const props = withDefaults(defineProps<BaseButtonProps>(), {
  variant: 'primary',
  size: 'md',
  disabled: false,
  loading: false,
  iconPosition: 'left',
  type: 'button',
})

const emit = defineEmits<{
  click: [event: MouseEvent]
}>()

const variantClasses: Record<string, string> = {
  primary:
    'bg-primary text-white rounded-md px-5 py-2.5 font-medium text-sm hover:shadow-sm active:scale-[0.98] disabled:opacity-50 w-full sm:w-auto',
  secondary:
    'bg-white text-primary border border-primary rounded-md px-5 py-2.5 font-medium text-sm hover:bg-bg-secondary hover:shadow-sm active:scale-[0.98] w-full sm:w-auto',
  danger: 'text-danger bg-transparent font-medium text-sm hover:underline active:scale-[0.98]',
  icon: 'w-9 h-9 rounded-full flex items-center justify-center hover:bg-bg-secondary active:scale-[0.95]',
  fab: 'bg-primary text-white rounded-lg px-5 py-2.5 font-medium text-sm fixed bottom-6 right-6 shadow-lg hover:shadow-xl active:scale-[0.98]',
}

const sizeClasses: Record<string, Record<string, string>> = {
  primary: {
    sm: 'px-3.5 py-1.5 text-xs',
    md: '',
    lg: 'px-7 py-3 text-base',
  },
  secondary: {
    sm: 'px-3.5 py-1.5 text-xs',
    md: '',
    lg: 'px-7 py-3 text-base',
  },
  danger: {
    sm: 'text-xs',
    md: '',
    lg: 'text-base',
  },
  icon: {
    sm: 'w-7 h-7',
    md: '',
    lg: 'w-11 h-11',
  },
  fab: {
    sm: 'px-3.5 py-1.5 text-xs',
    md: '',
    lg: 'px-7 py-3 text-base',
  },
}

const buttonClasses = computed(() => {
  const base = 'inline-flex items-center justify-center gap-2 transition-all duration-fast ease-smooth cursor-pointer'
  const disabledClass = props.disabled || props.loading ? 'pointer-events-none opacity-50' : ''
  const variant = variantClasses[props.variant] ?? variantClasses.primary
  const size = sizeClasses[props.variant]?.[props.size] ?? ''

  return [base, variant, size, disabledClass].filter(Boolean).join(' ')
})

function handleClick(event: MouseEvent) {
  if (props.disabled || props.loading) {
    event.preventDefault()
    return
  }
  emit('click', event)
}
</script>

<template>
  <button
    :type="props.type"
    :class="buttonClasses"
    :disabled="props.disabled || props.loading"
    :aria-disabled="props.disabled || props.loading || undefined"
    :aria-busy="props.loading || undefined"
    :aria-label="props.ariaLabel"
    @click="handleClick"
  >
    <!-- Loading spinner -->
    <span
      v-if="props.loading"
      class="inline-block w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"
      aria-hidden="true"
    />
    <span v-if="props.loading" class="sr-only">Loading</span>

    <!-- Icon left -->
    <template v-if="!props.loading && (props.icon || $slots.icon) && props.iconPosition === 'left'">
      <slot name="icon">
        <i :class="props.icon" aria-hidden="true" />
      </slot>
    </template>

    <!-- Default slot (label) -->
    <slot />

    <!-- Icon right -->
    <template v-if="!props.loading && (props.icon || $slots.icon) && props.iconPosition === 'right'">
      <slot name="icon">
        <i :class="props.icon" aria-hidden="true" />
      </slot>
    </template>
  </button>
</template>
